import {
  useAnimationStates,
  useShowInstructions,
} from '@backstage-components/base';
import {Image as ChakraImage, ImageProps} from '@chakra-ui/image';
import {AspectRatio, Box, BoxProps, Skeleton} from '@chakra-ui/react';
import {css} from '@emotion/react';
import {motion} from 'framer-motion';
import {useSubscription} from 'observable-hooks';
import {FC, useCallback, useEffect, useMemo, useState} from 'react';
import {ImageComponentDefinition, SizingProps} from './Image.types';
import {
  ComponentDefinition,
  defaultTransitionDuration,
  reactName as namespace,
} from './ImageDefinition';

const MotionImage = motion<ImageProps>(ChakraImage);
const MotionBox = motion<BoxProps>(Box);

export const ImageComponent: FC<ImageComponentDefinition> = (definition) => {
  const {id} = definition;
  const {
    alt,
    src,
    sizing,
    animationStates,
    styleAttr,
    objectFit,
    objectPosition,
    aspectRatioImage,
  } = definition.props;

  const {useAspectRatioImage, ratio, skeletonColor, transitionDuration} =
    aspectRatioImage || {};

  const [computedSrc, setComputedSrc] = useState(src.url);
  const [isLoaded, setIsLoaded] = useState(false);
  const [animationComplete, setAnimationComplete] = useState(false);
  const {observable, broadcast} = useShowInstructions(
    ComponentDefinition.instructions,
    definition
  );
  const motionOptions = useAnimationStates(
    observable,
    broadcast,
    animationStates
  );

  const onClick = useCallback(() => {
    broadcast({type: `${namespace}:on-click`, meta: {}});
  }, [broadcast]);

  const onLoad = useCallback(() => {
    broadcast({type: `${namespace}:on-load`, meta: {}});
    setIsLoaded(true);
  }, [broadcast]);

  useEffect(() => {
    setComputedSrc(src.url);
  }, [src.url]);

  useSubscription(observable, (inst) => {
    switch (inst.type) {
      case 'Image:change':
        setComputedSrc(inst.meta?.src);
        setIsLoaded(false);
        setAnimationComplete(false);
        break;
      case 'Image:click':
        onClick();
        break;
      default:
        break;
    }
  });

  const styles = css`
    ${definition.style}
    ${styleAttr}
  `;

  const ratioHeight = ratio?.height;
  const ratioWidth = ratio?.width;
  const withAspectRatio =
    typeof ratioHeight !== 'undefined' && typeof ratioWidth !== 'undefined';

  const aspectRatio =
    useAspectRatioImage && withAspectRatio
      ? ratioWidth / ratioHeight
      : undefined;

  const sizingProps: SizingProps = useMemo(
    () => ({
      width: sizing?.width || '100%',
      height: sizing?.height,
      minWidth: sizing?.minWidth,
      maxWidth: sizing?.maxWidth,
      minHeight: sizing?.minHeight,
      maxHeight: sizing?.maxHeight,
    }),
    [sizing]
  );

  let parentProps = sizingProps;

  if (withAspectRatio) {
    parentProps = {
      ...sizingProps,
      maxHeight: undefined,
      minHeight: undefined,
    };
  }

  const transitionTime = transitionDuration
    ? parseFloat(transitionDuration)
    : defaultTransitionDuration;
  const loadImage = {
    visible: {
      opacity: 1,
      scale: 1,
      transition: {duration: transitionTime, ease: 'easeOut'},
    },
    hidden: {
      opacity: 0,
      scale: 1.1,
      transition: {duration: transitionTime, ease: 'easeOut'},
    },
  };

  const imageContent = useMemo(() => {
    const aspectRatioSizingProps = {
      objectFit: objectFit,
      objectPosition: objectPosition,
      width: objectFit !== 'none' ? '100%' : undefined,
      height: objectFit !== 'none' ? '100%' : undefined,
    };

    const defaultSizingProps = {
      objectFit: objectFit,
      objectPosition: objectPosition,
      width: sizing?.width,
      height: sizing?.height,
      minWidth: sizing?.minWidth,
      maxWidth: sizing?.maxWidth,
      minHeight: sizing?.minHeight,
      maxHeight: sizing?.maxHeight,
    };

    return (
      <MotionImage
        id={id}
        role="img"
        data-testid="image-element"
        src={computedSrc}
        alt={alt}
        onClick={onClick}
        onLoad={onLoad}
        {...motionOptions}
        {...(aspectRatio ? aspectRatioSizingProps : defaultSizingProps)}
        css={!aspectRatio && styles}
      />
    );
  }, [
    objectFit,
    objectPosition,
    sizing?.width,
    sizing?.height,
    sizing?.minWidth,
    sizing?.maxWidth,
    sizing?.minHeight,
    sizing?.maxHeight,
    id,
    computedSrc,
    alt,
    onClick,
    onLoad,
    motionOptions,
    aspectRatio,
    styles,
  ]);

  if (!aspectRatio) {
    return imageContent;
  }

  return (
    <Box
      data-testid="image-container"
      position="relative"
      overflow="hidden"
      css={styles}
      {...parentProps}
    >
      {!animationComplete && (
        <Skeleton
          data-testid="image-skeleton"
          startColor={skeletonColor?.startColor}
          endColor={skeletonColor?.endColor}
          height="100%"
          width="100%"
          position="absolute"
          top="0"
          left="0"
        />
      )}

      <AspectRatio
        maxW="100%"
        data-testid="image-aspect-ratio"
        ratio={aspectRatio}
      >
        {/* When the image fades in, it needs to be centered, top and left need to overwrite Chakras styles */}
        <Box
          top="50% !important"
          left="50% !important"
          transform="translate(-50%, -50%)"
        >
          <MotionBox
            initial="hidden"
            animate={isLoaded ? 'visible' : 'hidden'}
            variants={loadImage}
            onAnimationComplete={() => setAnimationComplete(true)}
            width="100%"
            height="100%"
          >
            {imageContent}
          </MotionBox>
        </Box>
      </AspectRatio>
    </Box>
  );
};
